import { Trans, useLingui } from "@lingui/react/macro";
import type { Connection, DrokToolkit } from "@rakazo/contracts";
import { drokToolkitKey } from "@rakazo/contracts";
import { waitForConnection } from "@rakazo/core";
import { Button, Dialog, DialogClose, DialogContent, DialogTitle } from "@rakazo/ui-web";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useQueryUserId, useSpaceQueries } from "../lib/query";
import { useSpaceRpc } from "../lib/space-rpc";
import { errorText } from "../lib/user-error";
import type { ShellOverlay } from "../pages/shell-overlay";
import { EmployeeMcpConnections } from "./employee-mcp-connections";
import { drokRpc } from "./rpc";
import { useDrokCopies } from "./use-drok-copies";

const NO_CONNECTIONS: Connection[] = [];

/** The toolkits any of the caller's copies allow, once each; the server accepts exactly these. */
export function allowedToolkits(
  copies: readonly { toolkits: readonly DrokToolkit[] }[] | undefined,
): DrokToolkit[] {
  const seen = new Map<string, DrokToolkit>();
  for (const copy of copies ?? []) {
    for (const toolkit of copy.toolkits) {
      const key = drokToolkitKey(toolkit.slug);
      if (!seen.has(key)) seen.set(key, toolkit);
    }
  }
  return [...seen.values()];
}

function OverlayFrame({
  title,
  closeLabel,
  onClose,
  testId,
  children,
}: {
  title: string;
  closeLabel: string;
  onClose: () => void;
  testId: string;
  children: ReactNode;
}) {
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        data-testid={testId}
        showCloseButton={false}
        className="flex max-h-[min(640px,calc(100%-2rem))] w-[560px] max-w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-[560px]"
      >
        <div className="flex items-start justify-between px-6 pt-6">
          <DialogTitle className="text-heading2 font-medium text-foreground">{title}</DialogTitle>
          <DialogClose render={<Button variant="ghost" size="icon-sm" aria-label={closeLabel} />}>
            <X />
          </DialogClose>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-4">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The apps an employee can connect, taken from their copies' templates (names and logos come from
 * the template, never from the IT-only catalog), and the apps already connected, with revoke.
 */
export function EmployeeConnectionsOverlay({ onClose }: { onClose: () => void }) {
  const { t } = useLingui();
  const rpc = useSpaceRpc();
  const queries = useSpaceQueries();
  const queryClient = useQueryClient();
  const userId = useQueryUserId();
  const list = useQuery(queries.connections.list.queryOptions());
  const connections = (list.data ?? NO_CONNECTIONS).filter((row) => row.status !== "revoked");
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const copies = useDrokCopies();
  const toolkits = allowedToolkits(copies.copies);
  const mcp = useQuery({
    queryKey: ["drok", `user:${userId ?? ""}`, "copies.mcpConnections"],
    queryFn: () => drokRpc.copies.mcpConnections(),
    enabled: copies.copies !== undefined,
  });
  const hasMcp = (mcp.data?.length ?? 0) > 0;
  const [connecting, setConnecting] = useState<string | null>(null);
  const attempt = useRef<AbortController | null>(null);
  useEffect(() => () => attempt.current?.abort(), []);

  async function connect(toolkit: DrokToolkit) {
    attempt.current?.abort();
    const controller = new AbortController();
    attempt.current = controller;
    setConnecting(toolkit.slug);
    setError(null);
    try {
      const started = await rpc.connections.begin({
        connectorId: "composio",
        provider: toolkit.slug,
        displayName: toolkit.name,
      });
      if (started.authorizationUrl) {
        window.open(started.authorizationUrl, "rakazo-app-connect", "popup,width=560,height=720");
      }
      const result = await waitForConnection(
        started.connectionId,
        (input) => rpc.connections.complete(input, { signal: controller.signal }),
        { signal: controller.signal, attempts: 60 },
      );
      if (result.status === "aborted") return;
      if (result.status !== "connected") setError(t`Authorization timed out. Please try again.`);
      await queryClient.invalidateQueries({ queryKey: queries.connections.list.queryKey() });
    } catch (err) {
      if (!controller.signal.aborted) setError(errorText(err, t`Could not authorize this app`));
    } finally {
      if (attempt.current === controller) {
        attempt.current = null;
        setConnecting(null);
      }
    }
  }

  async function revoke(row: Connection) {
    setPending(row.id);
    setError(null);
    try {
      await rpc.connections.revoke({ connectionId: row.id });
      await queryClient.invalidateQueries({ queryKey: queries.connections.list.queryKey() });
    } catch (err) {
      setError(errorText(err, t`Could not revoke connection`));
    } finally {
      setPending(null);
    }
  }

  return (
    <OverlayFrame
      title={t`Connected apps`}
      closeLabel={t`Close connected apps`}
      onClose={onClose}
      testId="employee-connections"
    >
      {toolkits.length === 0 && !hasMcp ? (
        <p className="text-[13px] text-muted-foreground">
          <Trans>Ask IT to connect an app.</Trans>
        </p>
      ) : (
        <ul className="divide-y divide-border" data-testid="employee-toolkits">
          {toolkits.map((toolkit) => {
            const key = drokToolkitKey(toolkit.slug);
            const connected = connections.some(
              (row) => row.status === "connected" && drokToolkitKey(row.provider) === key,
            );
            return (
              <li key={key} className="flex items-center justify-between gap-3 py-2.5">
                <span className="flex min-w-0 items-center gap-3">
                  {toolkit.logo ? (
                    <img
                      src={toolkit.logo}
                      alt=""
                      className="h-8 w-8 rounded-lg bg-favicon-plate object-contain p-1"
                    />
                  ) : null}
                  <span className="truncate text-[14.5px] text-foreground" dir="auto">
                    {toolkit.name}
                  </span>
                </span>
                {connected ? (
                  <span className="text-[13px] text-muted-foreground">
                    <Trans>Connected</Trans>
                  </span>
                ) : (
                  <Button
                    size="sm"
                    disabled={connecting !== null}
                    onClick={() => void connect(toolkit)}
                  >
                    {connecting === toolkit.slug ? t`Waiting…` : t`Connect`}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {list.isError ? (
        <p className="mt-3 text-[13px] text-destructive">
          <Trans>Could not load</Trans>
        </p>
      ) : null}
      {!list.isPending && !list.isError && connections.length === 0 && !hasMcp ? (
        <p className="mt-3 text-sm text-muted-foreground">
          <Trans>No apps connected</Trans>
        </p>
      ) : null}
      <ul className="mt-3 divide-y divide-border">
        {connections.map((row) => (
          <li key={row.id} className="flex items-center justify-between gap-3 py-2.5">
            <span className="min-w-0 truncate text-[14.5px] text-foreground" dir="auto">
              {row.displayName}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pending === row.id}
              onClick={() => void revoke(row)}
            >
              <Trans>Revoke</Trans>
            </Button>
          </li>
        ))}
      </ul>
      <EmployeeMcpConnections connections={mcp.data ?? []} onChanged={() => mcp.refetch()} />
      {mcp.isError ? (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {errorText(mcp.error)}
        </p>
      ) : null}
      {error ? <p className="mt-3 text-[13px] text-destructive">{error}</p> : null}
    </OverlayFrame>
  );
}

/** The skills available to an employee, read-only. Skills are authored by IT. */
export function EmployeeSkillsOverlay({ onClose }: { onClose: () => void }) {
  const { t } = useLingui();
  const queries = useSpaceQueries();
  const list = useQuery(queries.agentSkills.list.queryOptions());
  const skills = list.data ?? [];
  return (
    <OverlayFrame
      title={t`Skills`}
      closeLabel={t`Close skills`}
      onClose={onClose}
      testId="employee-skills"
    >
      {list.isError ? (
        <p className="text-[13px] text-destructive">
          <Trans>Could not load</Trans>
        </p>
      ) : null}
      {!list.isPending && !list.isError && skills.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          <Trans>No skills yet</Trans>
        </p>
      ) : null}
      <ul className="divide-y divide-border">
        {skills.map((skill) => (
          <li key={skill.id} className="py-2.5">
            <p className="truncate text-[14.5px] font-medium text-foreground" dir="auto">
              {skill.name}
            </p>
            <p className="mt-0.5 text-[12.5px] text-muted-foreground" dir="auto">
              {skill.description}
            </p>
          </li>
        ))}
      </ul>
    </OverlayFrame>
  );
}

/** MCP deep links open the employee's constrained connections view, never IT's server editor. */
export function EmployeeAppOverlays({
  overlay,
  setOverlay,
}: {
  overlay: ShellOverlay;
  setOverlay: (next: ShellOverlay) => void;
}) {
  const closeOverlay = () => setOverlay(null);
  if (overlay?.kind === "integrations" || overlay?.kind === "mcp")
    return <EmployeeConnectionsOverlay onClose={closeOverlay} />;
  if (overlay?.kind === "skills") return <EmployeeSkillsOverlay onClose={closeOverlay} />;
  return null;
}
